import { cartLineItemKey } from "../keys";
import { hiddenModeRefusal } from "../validations";
import { readHidden, writeHidden } from "../visibility";
import type { CustomizationHandler } from "./types";

/**
 * The quantity control of each cart line item — the −/+ buttons and the
 * input — in the cart drawer and on the cart page alike.
 *
 * ## The selector
 *
 * Three alternatives, every one under `.js-cart-item`:
 *
 * - `.cart-item-quantity` — the wrapper of the whole control in the 17
 *   themes built on the shared `form-input.tpl` (amazonas, atlantico, bahia,
 *   baires, base, brasilia, cali, cubo, flex, guadalajara, idea, lima,
 *   morelia, recife, rio, toluca, uyuni). It holds the control only: price,
 *   subtotal and the delete button are siblings;
 * - `.cart-quantity-input-container` and `.js-cart-quantity-btn` — the input
 *   and the buttons of luxury, material, new_linkedman, style and zen. Their
 *   cart-page wrapper (`li.cart-quantity`) also holds the mobile name and
 *   subtotal, so the pieces are hidden instead of the wrapper.
 *
 * `.js-cart-item` is the root of every line item in all 22 themes, and
 * nothing else. It is what keeps `.cart-quantity-input-container` from
 * matching the product page of style and zen, which use it there too.
 *
 * Two of the three are not `js-*` classes: no single `js-*` hook wraps the
 * whole control across the themes (`.js-cart-quantity-container` wraps only
 * the input). The scope is positive, so a theme that renames them simply
 * stops matching.
 *
 * **It matches several elements per item**, as `product-grid-installments`
 * does: the 12 themes that repeat `cart-item-quantity` on the inner group
 * match it twice, plus their buttons. Hiding the outer already hides the
 * rest, and every element keeps its own snapshot, so `reset` stays exact; the
 * only visible effect is the `applied` count.
 *
 * ## Layout
 *
 * On the cart page of amazonas, cali, rio, lima and morelia the wrapper is a
 * grid column (`col-md-3`) under a "Cantidad" header, so hiding it shifts the
 * unit price and subtotal left of their headers. The drawer has no headers.
 *
 * ## Durability
 *
 * Changing a quantity patches the input's value and leaves the line item in
 * place, and the spinner's inline `display` loses to the class's
 * `!important`. Two things do replace line items, and drop the class with
 * them, so the app re-applies on `cart:update`: adding to the cart re-renders
 * the drawer's list, and Core's gift updater re-inserts gift items on every
 * cart update.
 *
 * ## Per item
 *
 * A resolver runs once per item of `cart.items`, and `identify` maps its
 * result back through the `.js-cart-item`'s `data-item-id`, the line item id
 * the state reports, so an app can lock the quantity of some items only (a
 * bundle's components, say).
 *
 * @see ./README.md
 */
export const cartItemQuantity: CustomizationHandler<"cart-item-quantity"> = {
	host: "storefront",
	selector: [
		".js-cart-item .cart-item-quantity",
		".js-cart-item .js-cart-quantity-btn",
		".js-cart-item .cart-quantity-input-container",
	].join(", "),

	read(element) {
		return { hidden: readHidden(element) };
	},

	operation(element, options) {
		if (options.hidden !== undefined) writeHidden(element, options.hidden);
	},

	values(element) {
		return { hidden: readHidden(element) };
	},

	identify: cartLineItemKey,

	validate(_element, options) {
		if (options.hidden !== undefined) {
			const refusal = hiddenModeRefusal(options.hidden);
			if (refusal) return `refused "hidden": ${refusal}`;
		}

		return null;
	},
};
